import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, ShieldCheck, Sparkles } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../services/AuthContext';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDevelopment ? 'http://localhost:7777' : '');

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDevLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Dev login failed');
      }

      const data = await response.json();
      await login(data.accessProfile || null);
      window.location.href = '/';
    } catch (err) {
      console.error('[LOGIN] Dev login failed:', err);
      setError(err instanceof Error ? err.message : 'Dev login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = async (credentialResponse: any) => {
    const googleIdToken = credentialResponse.credential;

    if (!googleIdToken) {
      setError('No token received from Google');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ idToken: googleIdToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      await login(data.accessProfile || null);
      window.location.href = '/';
    } catch (err) {
      console.error('[LOGIN] Failed to exchange token:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    setError('Google authentication failed');
  };

  const featureHighlights = [
    {
      icon: FileText,
      title: 'Editorial transcript access',
      body: 'Open the archive, jump between sessions, and use the transcript tools reserved for signed-in users.',
    },
    {
      icon: ShieldCheck,
      title: 'Organization-aware permissions',
      body: 'Your Google account is mapped to the correct MLST organization and role on sign-in.',
    },
    {
      icon: Sparkles,
      title: 'A cleaner workflow',
      body: 'Move from public reading to the full ReScript experience without changing products.',
    },
  ];

  return (
    <div className="brand-shell brand-grid">
      <div className="relative z-10 flex min-h-screen items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-[minmax(0,1.05fr)_460px]">
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="brand-card overflow-hidden bg-brand-navy px-8 py-10 text-brand-cloud sm:px-10 sm:py-12"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-[1.35rem] bg-white/10 shadow-brand">
                <img
                  src="/logos/combination_mark_yellow.svg"
                  alt="Machine Learning Street Talk"
                  className="h-8 w-auto"
                />
              </div>
              <div>
                <p className="font-headlines text-[0.68rem] font-bold uppercase tracking-[0.32em] text-brand-cloud/72">
                  Machine Learning Street Talk
                </p>
                <p className="mt-1 font-headlines text-2xl font-bold tracking-tight text-brand-yellow">ReScript</p>
              </div>
            </div>

            <p className="mt-10 brand-kicker border-white/15 bg-white/10 text-brand-cloud">Member Sign-In</p>
            <h1 className="mt-6 max-w-2xl font-headlines text-5xl font-bold tracking-tight text-brand-cloud sm:text-6xl">
              Sign into the MLST transcript room.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-brand-cloud/74">
              Use your Google account to move from the public editorial archive into the full MLST workspace with
              organization-aware permissions.
            </p>

            <div className="mt-10 space-y-4">
              {featureHighlights.map(({ icon: Icon, title, body }) => (
                <div key={title} className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] px-5 py-5">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-navy">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-headlines text-lg font-bold text-brand-cloud">{title}</h2>
                      <p className="mt-2 text-sm leading-7 text-brand-cloud/72">{body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="brand-card p-8 sm:p-10"
          >
            <p className="brand-kicker">Authentication</p>
            <h2 className="brand-highlight-text mt-6 font-headlines text-3xl font-bold tracking-tight">
              Continue with Google
            </h2>
            <p className="mt-4 text-base leading-8 text-brand-navy/68">
              Sign in with the Google account your MLST organization admin has approved. If you are not approved yet,
              you will be guided to the access request state after sign-in.
            </p>

            {error && (
              <div className="mt-6 rounded-[1.25rem] border border-red-300/70 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="mt-8 flex flex-col items-start gap-4">
              {isLoading ? (
                <div className="flex items-center gap-3 rounded-full border border-brand-navy/10 bg-white px-5 py-3 text-sm text-brand-navy/70">
                  <span className="brand-spinner h-5 w-5 !border-2" />
                  Authenticating...
                </div>
              ) : (
                <>
                  <GoogleLogin
                    onSuccess={handleSuccess}
                    onError={handleError}
                    theme="outline"
                    shape="pill"
                    size="large"
                    text="continue_with"
                    logo_alignment="left"
                  />
                  {isDevelopment && (
                    <button onClick={() => void handleDevLogin()} className="brand-button-secondary">
                      Dev login
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="mt-10">
              <button onClick={() => navigate('/')} className="brand-button-ghost">
                <ArrowLeft className="h-4 w-4" />
                Back to archive
              </button>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default Login;
